import { Alert, Button, Card, DatePicker, Form, Input, InputNumber, Modal, Space, message } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError } from '../../api/errors';
import { ProjectStatus, changeProjectStatus, recordProjectAcceptance } from '../../api/projects';
import type { ProjectDetail, ProjectStatusValue } from '../../api/projects';
import { formatDate, projectStatusLabel } from './projectDisplay';

/** Giá trị của biểu mẫu ghi nhận nghiệm thu. */
interface AcceptanceFormValues {
  readonly acceptanceDate: Dayjs;
  readonly warrantyMonths: number;
}

/**
 * Thẻ đổi trạng thái dự án và ghi nhận nghiệm thu.
 *
 * <b>Hiện nút cho từng bước chuyển thay vì một ô chọn trạng thái.</b> Ô chọn buộc người dùng biết
 * tám trạng thái và tự chọn đúng cái tiếp theo; nút thì nói thẳng việc họ sắp làm. Vẫn không cưỡng
 * chế đi tuần tự — mọi bước hợp lệ đều hiện, vì một dự án phần mềm thuần không có bước lắp đặt tại
 * hiện trường.
 *
 * <b>Ghi nhận nghiệm thu là một thao tác riêng, không gộp vào đổi trạng thái.</b> Biên bản nghiệm
 * thu có khi ký từng phần, và có dự án nghiệm thu xong vẫn còn hạng mục tồn đọng phải xử lý trước
 * khi coi là bước vào giai đoạn bảo hành.
 */
export function ProjectStatusCard({
  project,
  canChangeStatus,
  canRecordAcceptance,
  onUpdated,
}: {
  readonly project: ProjectDetail;
  readonly canChangeStatus: boolean;
  readonly canRecordAcceptance: boolean;
  readonly onUpdated: (updated: ProjectDetail) => void;
}): ReactNode {
  const [acceptanceForm] = Form.useForm<AcceptanceFormValues>();

  const [isAcceptanceOpen, setIsAcceptanceOpen] = useState(false);
  const [isHoldOpen, setIsHoldOpen] = useState(false);
  const [holdReason, setHoldReason] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const isFinal =
    project.status === ProjectStatus.Closed || project.status === ProjectStatus.Cancelled;

  async function changeStatus(status: ProjectStatusValue, reason: string | null): Promise<void> {
    setIsSaving(true);

    try {
      const updated = await changeProjectStatus(project.id, { status, holdReason: reason });

      onUpdated(updated);
      setIsHoldOpen(false);
      setHoldReason('');
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không đổi được trạng thái.');
    } finally {
      setIsSaving(false);
    }
  }

  async function submitAcceptance(values: AcceptanceFormValues): Promise<void> {
    setIsSaving(true);

    try {
      const updated = await recordProjectAcceptance(project.id, {
        acceptanceDate: values.acceptanceDate.format('YYYY-MM-DD'),
        warrantyMonths: values.warrantyMonths,
      });

      onUpdated(updated);
      setIsAcceptanceOpen(false);
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không ghi nhận được nghiệm thu.');
    } finally {
      setIsSaving(false);
    }
  }

  /** Những trạng thái đích hợp lý từ trạng thái hiện tại, trừ tạm dừng và hai trạng thái cuối. */
  const nextStatuses: readonly ProjectStatusValue[] = [
    ProjectStatus.InProgress,
    ProjectStatus.Installing,
    ProjectStatus.PendingAcceptance,
    ProjectStatus.UnderWarranty,
  ].filter((status) => status !== project.status);

  return (
    <Card size="small" title="Trạng thái">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {project.status === ProjectStatus.OnHold && project.holdReason !== null ? (
          <Alert type="warning" showIcon message={`Đang tạm dừng: ${project.holdReason}`} />
        ) : null}

        {isFinal ? (
          <Alert
            type="info"
            showIcon
            message={`Dự án đã ở trạng thái ${projectStatusLabel(project.status)} — đây là trạng thái cuối, không đổi được nữa.`}
            description="Cần làm tiếp thì mở dự án mới và ghi rõ nó nối tiếp dự án nào. Mở lại một dự án đã đóng sẽ làm hỏng mọi báo cáo đã chốt theo kỳ."
          />
        ) : null}

        <Space wrap>
          {nextStatuses.map((status) => (
            <Button
              key={status}
              disabled={!canChangeStatus || isFinal || isSaving}
              onClick={() => void changeStatus(status, null)}
            >
              Chuyển sang {projectStatusLabel(status).toLowerCase()}
            </Button>
          ))}

          {project.status === ProjectStatus.OnHold ? null : (
            <Button
              disabled={!canChangeStatus || isFinal || isSaving}
              onClick={() => { setIsHoldOpen(true); }}
            >
              Tạm dừng
            </Button>
          )}

          <Button
            disabled={!canRecordAcceptance || isSaving}
            onClick={() => {
              acceptanceForm.setFieldsValue({
                acceptanceDate: dayjs(),
                warrantyMonths: project.warrantyMonths ?? 12,
              });
              setIsAcceptanceOpen(true);
            }}
          >
            {project.acceptanceDate === null
              ? 'Ghi nhận nghiệm thu'
              : `Sửa nghiệm thu (${formatDate(project.acceptanceDate)})`}
          </Button>

          <Button
            danger
            disabled={!canChangeStatus || isFinal || isSaving}
            onClick={() => void changeStatus(ProjectStatus.Closed, null)}
          >
            Đóng dự án
          </Button>
          <Button
            danger
            disabled={!canChangeStatus || isFinal || isSaving}
            onClick={() => void changeStatus(ProjectStatus.Cancelled, null)}
          >
            Hủy dự án
          </Button>
        </Space>

        {!canChangeStatus ? (
          <Alert type="info" showIcon message="Bạn không có quyền đổi trạng thái dự án này." />
        ) : null}
      </Space>

      <Modal
        open={isHoldOpen}
        title="Tạm dừng dự án"
        okText="Tạm dừng"
        cancelText="Đóng"
        confirmLoading={isSaving}
        okButtonProps={{ disabled: holdReason.trim() === '' }}
        onCancel={() => { setIsHoldOpen(false); }}
        onOk={() => void changeStatus(ProjectStatus.OnHold, holdReason)}
      >
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="Bắt buộc ghi lý do"
            description="Người quay lại dự án sau vài tháng cần biết vì sao nó dừng, và nguyên nhân thường nằm ở phía khách — thông tin đó quyết định cách nối lại."
          />
          <Input.TextArea
            rows={3}
            value={holdReason}
            placeholder="Khách chưa dừng được dây chuyền để lắp đặt."
            onChange={(event) => { setHoldReason(event.target.value); }}
          />
        </Space>
      </Modal>

      <Modal
        open={isAcceptanceOpen}
        title="Ghi nhận nghiệm thu"
        okText="Lưu"
        cancelText="Đóng"
        confirmLoading={isSaving}
        onCancel={() => { setIsAcceptanceOpen(false); }}
        onOk={() => { acceptanceForm.submit(); }}
      >
        <Form
          form={acceptanceForm}
          layout="vertical"
          onFinish={(values) => void submitAcceptance(values)}
        >
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Thao tác này không tự đổi trạng thái"
            description="Hai việc tách nhau có chủ ý: biên bản nghiệm thu có khi ký từng phần, và có dự án nghiệm thu xong vẫn còn hạng mục tồn đọng phải xử lý."
          />
          <Form.Item
            name="acceptanceDate"
            label="Ngày khách ký biên bản nghiệm thu"
            rules={[{ required: true, message: 'Chưa chọn ngày.' }]}
            extra="Không được là ngày ở tương lai: đây là mốc bắt đầu tính bảo hành."
          >
            <DatePicker
              format="DD/MM/YYYY"
              style={{ width: '100%' }}
              disabledDate={(current) => current.isAfter(dayjs(), 'day')}
            />
          </Form.Item>
          <Form.Item
            name="warrantyMonths"
            label="Số tháng bảo hành"
            rules={[{ required: true, message: 'Chưa nhập số tháng bảo hành.' }]}
            extra="Số 0 nghĩa là dự án này không có bảo hành, ví dụ dịch vụ khảo sát."
          >
            <InputNumber<number> min={0} max={120} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
